import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Calendar,
  X,
  Columns,
  Check,
  RotateCcw,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatUSD } from '../../utils/formatAmount';
import { useIsMobile } from '../../hooks/useMediaQuery';

const DataTable = ({
  columns,
  data,
  searchable = true,
  filterable = true,
  // Bounds for the range picker. A history table passes maxDate={new Date()} so
  // a range can never be set into the future, where by definition no row exists.
  minDate,
  maxDate,
  sortable = true,
  paginated = true,
  pageSize = 10,
  emptyMessage = 'No data available',
  onRowClick,
  showColumnToggle = true,
  serverSidePagination = false,
  totalRecords = 0,
  currentServerPage = 1,
  onPageChange,
  onPageSizeChange,
  onDateRangeChange,
  controlledDateRange = null,
  // 'inline' renders one <select> per filterable column, which is the default
  // every existing caller gets. 'quick' renders the single Quick Filters button
  // and panel used on the Transactions page: grouped sections, a tick in a fixed
  // slot, a live count beside each option and a Reset.
  filterVariant = 'inline',
}) => {
  const isMobile = useIsMobile();
  const [showQuickFilters, setShowQuickFilters] = useState(false);
  const quickFilterRef = useRef(null);

  // A panel that only closes via its own button is a panel users leave open.
  useEffect(() => {
    if (!showQuickFilters) return undefined;
    const onDocClick = (e) => {
      if (quickFilterRef.current && !quickFilterRef.current.contains(e.target)) {
        setShowQuickFilters(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowQuickFilters(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showQuickFilters]);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [internalDateRange, setInternalDateRange] = useState([null, null]);

  const dateRange = controlledDateRange || internalDateRange;
  const setDateRange = controlledDateRange ? () => {} : setInternalDateRange;
  const [startDate, endDate] = dateRange;
  const [selectedPageSize, setSelectedPageSize] = useState(pageSize);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({});

  useEffect(() => {
    const initialVisibility = {};
    columns.forEach((col) => {
      initialVisibility[col.accessor] = col.visible !== false;
    });
    setVisibleColumns(initialVisibility);
  }, [columns]);

  const displayedColumns = columns.filter((col) => visibleColumns[col.accessor] !== false);

  const filterableColumns = columns.filter((col) => col.filterable);

  // Counted over `data`, not the filtered result: if the counts responded to the
  // current selection, every other option would read 0 the moment one was
  // picked, and the panel would stop being a map of what is there.
  const filterCounts = useMemo(() => {
    const out = {};
    for (const col of filterableColumns) {
      const tally = {};
      for (const row of data || []) {
        const key = String(row[col.accessor] ?? '').toLowerCase();
        tally[key] = (tally[key] || 0) + 1;
      }
      out[col.accessor] = tally;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columns]);

  const activeFilterCount = Object.values(filters).filter((v) => v && v !== 'all').length;

  const processedData = useMemo(() => {
    let result = [...data];

    if (search && searchable) {
      const searchLower = search.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const value = row[col.accessor];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchLower);
        })
      );
    }

    if (startDate && endDate && !serverSidePagination) {
      // The picker hands back midnight for both ends, so comparing a timestamp
      // against `endDate` directly excluded everything that happened on the last
      // day of the range — pick "Sep 28 – Sep 30" and a conversion at 16:52 on
      // the 30th disappeared. Both edges are widened to cover the whole day the
      // user actually chose.
      const from = new Date(startDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(endDate);
      to.setHours(23, 59, 59, 999);

      result = result.filter((row) => {
        const dateCol = columns.find((col) => col.type === 'date');
        if (!dateCol) return true;
        const rowDate = new Date(row[dateCol.accessor]);
        if (Number.isNaN(rowDate.getTime())) return false;
        return rowDate >= from && rowDate <= to;
      });
    }

    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key] !== 'all') {
        result = result.filter((row) => {
          const value = row[key];
          return String(value).toLowerCase() === filters[key].toLowerCase();
        });
      }
    });

    if (sortConfig.key && sortable) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const comparison = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, search, sortConfig, filters, startDate, endDate, columns, searchable, sortable]);

  const effectivePage = serverSidePagination ? currentServerPage : currentPage;
  const totalItems = serverSidePagination ? totalRecords : processedData.length;
  const totalPages = Math.ceil(totalItems / selectedPageSize);

  const paginatedData = paginated
    ? serverSidePagination
      ? processedData 
      : processedData.slice(
          (currentPage - 1) * selectedPageSize,
          currentPage * selectedPageSize
        )
    : processedData;

  const handleSort = (key) => {
    if (!sortable) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setDateRange([null, null]);
    setSearch('');
    setCurrentPage(1);
    if (serverSidePagination && onDateRangeChange) {
      onDateRangeChange(null, null);
    }
  };

  const renderCell = (row, column) => {
    const value = row[column.accessor];

    if (column.render) {
      return column.render(value, row);
    }

    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString() : '-';
    }

    if (column.type === 'currency') {
      return value !== null && value !== undefined
        ? `$${formatUSD(value)}`
        : '-';
    }

    if (column.type === 'status') {
      return (
        <span className={`status-badge ${String(value).toLowerCase()}`}>
          {value}
        </span>
      );
    }

    return value ?? '-';
  };

  const hasActiveFilters =
    search ||
    startDate ||
    endDate ||
    Object.values(filters).some((v) => v && v !== 'all');

  return (
    <div className="data-table-container">
      <div className="table-controls">
        <div className="controls-left asds">
          {searchable && (
            <div className="search-box">
              <Search className='search' size={18} />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              {search && (
                <button className="clear-search" onClick={() => setSearch('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          {filterable && (
            <>
              <div className="date-filter">
                <Calendar size={18} />
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [newStart, newEnd] = update;

                  
                    if (controlledDateRange && onDateRangeChange) {
                      onDateRangeChange(newStart, newEnd);
                      if ((newStart && newEnd) || (!newStart && !newEnd)) {
                        setCurrentPage(1);
                      }
                    } else {
                      setDateRange(update);
                      if (serverSidePagination && onDateRangeChange) {
                        if ((newStart && newEnd) || (!newStart && !newEnd)) {
                          setCurrentPage(1);
                          onDateRangeChange(newStart, newEnd);
                        }
                      } else {
                        setCurrentPage(1);
                      }
                    }
                  }}
                  placeholderText="Select date range"
                  isClearable
                  minDate={minDate}
                  maxDate={maxDate}
                  dateFormat="dd/MM/yyyy"
                  className="date-picker-input"
                />
              </div>

              {filterVariant === 'quick' && filterableColumns.length > 0 ? (
                <div className="dt-quick-filter" ref={quickFilterRef}>
                  <button
                    type="button"
                    className={`dtqf-trigger ${activeFilterCount ? 'is-filtered' : ''}`}
                    onClick={() => setShowQuickFilters((v) => !v)}
                    aria-expanded={showQuickFilters}
                    aria-haspopup="true"
                  >
                    <Filter size={16} />
                    Quick Filters
                    {activeFilterCount > 0 && (
                      <span className="dtqf-badge">{activeFilterCount}</span>
                    )}
                  </button>

                  {showQuickFilters && (
                    <div className="dtqf-panel">
                      {/* Says what is on and offers the way out. Without it the
                          only clue a filter is active is a tick further down. */}
                      <div className="dtqf-top">
                        <span className="dtqf-top-title">Filters</span>
                        {activeFilterCount > 0 && (
                          <button
                            type="button"
                            className="dtqf-reset"
                            onClick={() => {
                              setFilters({});
                              setCurrentPage(1);
                            }}
                          >
                            Reset
                          </button>
                        )}
                      </div>

                      {filterableColumns.map((col) => {
                        const selected = filters[col.accessor] || 'all';
                        const counts = filterCounts[col.accessor] || {};
                        // Options may declare a `group`; those that do are
                        // rendered under a subheading, in declaration order.
                        const groups = [];
                        for (const opt of col.filterOptions || []) {
                          const name = opt.group || '';
                          const last = groups[groups.length - 1];
                          if (last && last.name === name) last.items.push(opt);
                          else groups.push({ name, items: [opt] });
                        }

                        return (
                          <div key={col.accessor} className="dtqf-section">
                            <div className="dtqf-header">{col.header}</div>
                            <button
                              type="button"
                              className={`dtqf-item ${selected === 'all' ? 'active' : ''}`}
                              onClick={() => {
                                handleFilterChange(col.accessor, 'all');
                                setCurrentPage(1);
                              }}
                            >
                              <span className="dtqf-check">
                                {selected === 'all' && <Check size={14} />}
                              </span>
                              <span className="dtqf-label">All {col.header}</span>
                              <span className="dtqf-count">{(data || []).length}</span>
                            </button>

                            {groups.map((group, gi) => (
                              <React.Fragment key={group.name || gi}>
                                {group.name && <div className="dtqf-group">{group.name}</div>}
                                {group.items.map((opt) => {
                                  const active = selected === opt.value;
                                  const n = counts[String(opt.value).toLowerCase()] || 0;
                                  return (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      className={`dtqf-item ${active ? 'active' : ''} ${
                                        n === 0 ? 'is-empty' : ''
                                      }`}
                                      onClick={() => {
                                        handleFilterChange(col.accessor, opt.value);
                                        setCurrentPage(1);
                                      }}
                                    >
                                      {/* The tick has its own fixed slot so
                                          selecting one does not shove every
                                          label sideways. */}
                                      <span className="dtqf-check">
                                        {active && <Check size={14} />}
                                      </span>
                                      <span className="dtqf-label">{opt.label}</span>
                                      <span className="dtqf-count">{n}</span>
                                    </button>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                filterableColumns.map((col) => (
                  <div key={col.accessor} className="inline-filter">
                    <Filter size={18} />
                    <select
                      value={filters[col.accessor] || 'all'}
                      onChange={(e) => handleFilterChange(col.accessor, e.target.value)}
                      className="inline-filter-select"
                    >
                      <option value="all">All {col.header}</option>
                      {col.filterOptions?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))
              )}
            </>
          )}
        </div>

        <div className="controls-right">
          {showColumnToggle && (
            <div className="column-toggle-container">
              <button
                className={`column-toggle-btn ${showColumnDropdown ? 'active' : ''}`}
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              >
                <Columns size={18} />
                Columns
              </button>
              {showColumnDropdown && (
                <div className="column-dropdown">
                  <div className="column-dropdown-header">
                    <span>Show/Hide Columns</span>
                    <button
                      className="reset-columns-btn"
                      onClick={() => {
                        const reset = {};
                        columns.forEach((col) => {
                          reset[col.accessor] = true;
                        });
                        setVisibleColumns(reset);
                      }}
                    >
                      <RotateCcw size={14} />
                      Reset
                    </button>
                  </div>
                  <div className="column-dropdown-list">
                    {columns.map((col) => (
                      <label key={col.accessor} className="column-checkbox">
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.accessor] !== false}
                          onChange={() =>
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [col.accessor]: !prev[col.accessor],
                            }))
                          }
                        />
                        <span className="checkbox-custom">
                          {visibleColumns[col.accessor] !== false && <Check size={12} />}
                        </span>
                        {col.header}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              <X size={14} />
              Clear all
            </button>
          )}
          <span className="results-count">
            {processedData.length} result{processedData.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>


      {isMobile ? (
        <div className="table-cards">
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
              <div
                key={row.id || rowIndex}
                className={`table-card ${onRowClick ? 'clickable' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {displayedColumns.map((col) => {
                  const isActionsCol = col.accessor === 'actions' || col.type === 'actions';
                  return (
                    <div
                      key={col.accessor}
                      className={`table-card-row ${isActionsCol ? 'table-card-actions' : ''}`}
                    >
                      {!isActionsCol && <span className="table-card-label">{col.header}</span>}
                      <span className="table-card-value">{renderCell(row, col)}</span>
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>{emptyMessage}</p>
            </div>
          )}
        </div>
      ) : (
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {displayedColumns.map((col) => (
                <th
                  key={col.accessor}
                  className={`${sortable && col.sortable !== false ? 'sortable' : ''} ${
                    sortConfig.key === col.accessor ? 'sorted' : ''
                  }`}
                  onClick={() => col.sortable !== false && handleSort(col.accessor)}
                  style={{ width: col.width }}
                >
                  <div className="th-content">
                    <span>{col.header}</span>
                    {sortable && col.sortable !== false && (
                      <div className="sort-icons">
                        <ChevronUp
                          size={12}
                          className={
                            sortConfig.key === col.accessor &&
                            sortConfig.direction === 'asc'
                              ? 'active'
                              : ''
                          }
                        />
                        <ChevronDown
                          size={12}
                          className={
                            sortConfig.key === col.accessor &&
                            sortConfig.direction === 'desc'
                              ? 'active'
                              : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={onRowClick ? 'clickable' : ''}
                >
                  {displayedColumns.map((col) => (
                    <td key={col.accessor}>{renderCell(row, col)}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className="empty-row">
                <td colSpan={displayedColumns.length}>
                  <div className="empty-state">
                    <p>{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {paginated && (serverSidePagination ? totalItems > 0 : processedData.length > 0) && (
        <div className="table-pagination">
          <div className="pagination-info">
            <span>
              Showing {(effectivePage - 1) * selectedPageSize + 1} to{' '}
              {Math.min(effectivePage * selectedPageSize, totalItems)} of{' '}
              {totalItems} entries
            </span>
            <select
              value={selectedPageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setSelectedPageSize(newSize);
                if (serverSidePagination && onPageSizeChange) {
                  onPageSizeChange(newSize);
                } else {
                  setCurrentPage(1);
                }
              }}
              className="page-size-select rd-select"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => {
                if (serverSidePagination && onPageChange) {
                  onPageChange(1);
                } else {
                  setCurrentPage(1);
                }
              }}
              disabled={effectivePage === 1}
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => {
                if (serverSidePagination && onPageChange) {
                  onPageChange(effectivePage - 1);
                } else {
                  setCurrentPage((prev) => prev - 1);
                }
              }}
              disabled={effectivePage === 1}
            >
              <ChevronLeft size={18} />
            </button>

            <div className="page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (page >= effectivePage - 1 && page <= effectivePage + 1) return true;
                  return false;
                })
                .map((page, index, arr) => (
                  <React.Fragment key={page}>
                    {index > 0 && arr[index - 1] !== page - 1 && (
                      <span className="page-ellipsis">...</span>
                    )}
                    <button
                      className={`page-number ${effectivePage === page ? 'active' : ''}`}
                      onClick={() => {
                        if (serverSidePagination && onPageChange) {
                          onPageChange(page);
                        } else {
                          setCurrentPage(page);
                        }
                      }}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>

            <button
              className="pagination-btn"
              onClick={() => {
                if (serverSidePagination && onPageChange) {
                  onPageChange(effectivePage + 1);
                } else {
                  setCurrentPage((prev) => prev + 1);
                }
              }}
              disabled={effectivePage === totalPages || totalPages === 0}
            >
              <ChevronRight size={18} />
            </button>
            <button
              className="pagination-btn"
              onClick={() => {
                if (serverSidePagination && onPageChange) {
                  onPageChange(totalPages);
                } else {
                  setCurrentPage(totalPages);
                }
              }}
              disabled={effectivePage === totalPages || totalPages === 0}
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
